import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import ApiService from '../services/ApiService';
import AuthService from '../services/AuthService';
import Loader from '../components/Loader';
import PageHeader from '../components/ui/PageHeader';
import StatCard from '../components/ui/StatCard';
import StatusBadge from '../components/ui/StatusBadge';
import TrendChart from '../components/ui/TrendChart';
import Avatar from '../components/ui/Avatar';
import { BOOKING_STATUSES } from '../assets/utils/statusColors';
import { formatDate } from '../utils/helper';

const currency = process.env.REACT_APP_CURRENCY || 'AED';
const money = (v) => `${currency} ${Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const num = (v) => Number(v || 0).toLocaleString('en-US');

const PIPELINE_COLORS = {
    Requested: '#e8a317',
    Confirmed: '#2f80ed',
    'In-Progress': '#7c5cff',
    Completed: '#03b463',
    Cancelled: '#e5484d',
};

const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
};

const Dashboard = () => {
    const admin = AuthService.getCurrentUser() || {};
    const [isLoader, setIsLoader] = useState(true);
    const [stats, setStats] = useState({});
    const [charts, setCharts] = useState({ months: [], month_users: [], month_orders: [], month_revenue: [] });
    const [recent, setRecent] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await ApiService.request({ method: 'GET', url: 'getDashboard' });
                if (data.status) {
                    setStats(data.data.stats || {});
                    setCharts(data.data.charts || {});
                    setRecent(data.data.recent_bookings || []);
                    localStorage.setItem('map_key', data.data.map_key);
                } else {
                    toast.error(data.message);
                }
            } catch (error) {
                // ApiService already shows the error toast
            } finally {
                setIsLoader(false);
            }
        })();
    }, []);

    if (isLoader) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
                <Loader text="Loading dashboard..." size="40" />
            </div>
        );
    }

    const breakdown = stats.status_breakdown || {};
    const totalBookings = stats.total_jobs || 0;
    const sum = (arr = []) => arr.reduce((a, b) => a + (b || 0), 0);
    const year = new Date().getFullYear();

    return (
        <div className="hp-page">
            <PageHeader
                title={`${greeting()}, ${(admin.name || 'Admin').split(' ')[0]}`}
                subtitle={`Here's what's happening on Helpio today · ${formatDate(new Date(), 'dddd, DD MMM YYYY')}`}
                actions={(
                    <>
                        <Link to="/properties" className="btn btn-sm hp-btn-outline"><i className="bi bi-houses me-1"></i> Properties</Link>
                        <Link to="/bookings" className="btn btn-sm btn-primary"><i className="bi bi-calendar2-check me-1"></i> View bookings</Link>
                    </>
                )}
            />

            {/* KPIs */}
            <div className="row g-4 mb-4">
                <div className="col-sm-6 col-xl-3">
                    <StatCard label="Total bookings" value={num(totalBookings)} icon="bi-calendar2-check" tone="brand" to="/bookings"
                        foot={`${num(stats.active_jobs)} active right now`} />
                </div>
                <div className="col-sm-6 col-xl-3">
                    <StatCard label="Revenue" value={money(stats.total_revenue)} icon="bi-cash-stack" tone="info"
                        foot={`${money(sum(charts.month_revenue))} in ${year}`} />
                </div>
                <div className="col-sm-6 col-xl-3">
                    <StatCard label="Customers" value={num(stats.job_seekers)} icon="bi-people" tone="violet" to="/users"
                        foot={`${num(sum(charts.month_users))} joined in ${year}`} />
                </div>
                <div className="col-sm-6 col-xl-3">
                    <StatCard label="Partners" value={num((stats.service_providers || 0) + (stats.facilities || 0))} icon="bi-person-workspace" tone="warning" to="/companies"
                        foot={`${num(stats.service_providers)} providers · ${num(stats.facilities)} facilities`} />
                </div>
            </div>

            {/* Pipeline */}
            <div className="hp-card mb-4">
                <div className="hp-card-head">
                    <div>
                        <h3 className="hp-card-title">Booking pipeline</h3>
                        <p className="hp-card-sub">Where every booking currently stands</p>
                    </div>
                    <Link to="/bookings" className="btn btn-sm hp-btn-outline">All bookings</Link>
                </div>
                <div className="hp-card-body">
                    <div className="hp-bar mb-4" aria-hidden="true">
                        {BOOKING_STATUSES.map(s => (
                            <span key={s} style={{ width: `${totalBookings ? ((breakdown[s] || 0) / totalBookings) * 100 : 0}%`, background: PIPELINE_COLORS[s] }} />
                        ))}
                    </div>
                    <div className="hp-pipeline">
                        {BOOKING_STATUSES.map(s => (
                            <Link key={s} to={`/bookings?status=${s}`} className="hp-pipe">
                                <div className="hp-pipe-top"><span className="hp-pipe-dot" style={{ background: PIPELINE_COLORS[s] }} />{s}</div>
                                <div className="hp-pipe-value">{num(breakdown[s])}</div>
                            </Link>
                        ))}
                    </div>
                </div>
            </div>

            {/* Needs attention */}
            <div className="row g-4 mb-4">
                <div className="col-md-4">
                    <StatCard label="New requests to handle" value={num(stats.total_requests)} icon="bi-inbox" tone="warning" to="/bookings?status=Requested" />
                </div>
                <div className="col-md-4">
                    <StatCard label="Properties awaiting review" value={num(stats.pending_properties)} icon="bi-house-exclamation" tone="danger" to="/properties" />
                </div>
                <div className="col-md-4">
                    <StatCard label="Service categories" value={num(stats.total_categories)} icon="bi-grid-3x3-gap" tone="slate" to="/categories" />
                </div>
            </div>

            {/* Trends */}
            <div className="row g-4 mb-4">
                <div className="col-xl-8">
                    <div className="hp-card h-100">
                        <div className="hp-card-head">
                            <div>
                                <h3 className="hp-card-title">Revenue</h3>
                                <p className="hp-card-sub">Paid bookings per month, {year}</p>
                            </div>
                            <span className="fs-4 fw-bold">{money(sum(charts.month_revenue))}</span>
                        </div>
                        <div className="hp-card-body">
                            <TrendChart type="line" labels={charts.months} data={charts.month_revenue} label="Revenue" color="#2f80ed" format={money} />
                        </div>
                    </div>
                </div>
                <div className="col-xl-4">
                    <div className="hp-card h-100">
                        <div className="hp-card-head">
                            <div>
                                <h3 className="hp-card-title">Bookings</h3>
                                <p className="hp-card-sub">New bookings per month</p>
                            </div>
                            <span className="fs-4 fw-bold">{num(sum(charts.month_orders))}</span>
                        </div>
                        <div className="hp-card-body">
                            <TrendChart type="bar" labels={charts.months} data={charts.month_orders} label="Bookings" />
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-4">
                <div className="col-xl-8">
                    <div className="hp-card h-100">
                        <div className="hp-card-head pb-3">
                            <div>
                                <h3 className="hp-card-title">Recent bookings</h3>
                                <p className="hp-card-sub">The latest activity across all partners</p>
                            </div>
                            <Link to="/bookings" className="btn btn-sm hp-btn-outline">View all</Link>
                        </div>
                        <div className="table-responsive">
                            <table className="table hp-table table-hover align-middle mb-0">
                                <thead>
                                    <tr>
                                        <th>Booking</th>
                                        <th>Customer</th>
                                        <th>Provider</th>
                                        <th>Status</th>
                                        <th className="text-end">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recent.length > 0 ? recent.map(b => (
                                        <tr key={b._id}>
                                            <td>
                                                <Link to={`/bookings/${b._id}`} className="fw-bold text-dark text-hover-primary">#{b.job_code}</Link>
                                                <div className="text-muted fs-7">{b.service_id?.name || '—'} · {formatDate(b.createdAt, 'DD MMM')}</div>
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <Avatar src={b.user_id?.picture} name={b.user_id?.name} size={30} />
                                                    <span className="text-dark">{b.user_id?.name || '—'}</span>
                                                </div>
                                            </td>
                                            <td>{b.company_id?.name || <span className="text-muted">Unassigned</span>}</td>
                                            <td><StatusBadge status={b.status} /></td>
                                            <td className="text-end fw-semibold text-dark">{b.pay_amount != null ? money(b.pay_amount) : '—'}</td>
                                        </tr>
                                    )) : (
                                        <tr><td colSpan="5"><div className="hp-empty"><i className="bi bi-calendar2-x"></i>No bookings yet</div></td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <div className="col-xl-4">
                    <div className="hp-card h-100">
                        <div className="hp-card-head">
                            <div>
                                <h3 className="hp-card-title">New customers</h3>
                                <p className="hp-card-sub">Sign-ups per month</p>
                            </div>
                            <span className="fs-4 fw-bold">{num(sum(charts.month_users))}</span>
                        </div>
                        <div className="hp-card-body">
                            <TrendChart type="line" labels={charts.months} data={charts.month_users} label="Customers" color="#7c5cff" height={300} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
